import {useEffect} from 'react';
import {useTranslate} from 'hooks';
import {
  formatMillis,
  useAuthState,
  useMockState,
} from '@resurs-internal/rb-app-api';
import {useAuthTimer} from '@resurs-internal/rb-app-api/hooks';
import {DialogContent, Stack, Typography} from '@mui/material';
import {BaseDialog, Visibility} from '@resurs-internal/mui';
import {useNavigate} from '@tanstack/react-router';
import {IconBadge} from 'components';
import {useActivityMonitor} from '../../hooks/useActivityMonitor';
import {isOpenBankingCallbackPath} from '../../services/uri/isOpenBankingCallbackPath';

export const AuthRefreshDialog = () => {
  const navigate = useNavigate();
  const translate = useTranslate();
  const auth = useAuthState();
  const timer = useAuthTimer();
  const mock = useMockState();
  const activity = useActivityMonitor();

  const isLoggedIn = !!auth.state?.isLoggedIn;
  const open = isLoggedIn && activity.isAlmostExpired && !mock.isDemoMode;

  useEffect(() => {
    // logged out is handled by the _protected route guard
    if (!isLoggedIn) return;

    // if auth not required for path: do nothing
    const pathname = window.location.pathname;
    const isAuthPath = isAuthRequiredPath(pathname);
    if (!isAuthPath) return;

    if (timer.isRefreshExpired) {
      navigate({to: '/logout'});
      return;
    }

    // if activity expired: logout
    if (activity.isExpired) {
      navigate({to: '/logout'});
      return;
    }

    // if access token expired and activity is not: refresh session
    if (timer.isAccessExpired && !activity.isExpired) {
      auth.refresh.mutate();
      return;
    }
  }, [
    navigate,
    isLoggedIn,
    timer.isAccessExpired,
    timer.isRefreshExpired,
    activity.isExpired,
    auth.refresh,
  ]);

  const onRefresh = () => {
    auth.refresh.mutateAsync();
  };

  const onLogout = () => {
    navigate({to: '/logout'});
  };

  return (
    <BaseDialog
      sx={{zIndex: 99999999999}}
      open={open}
      onClose={onRefresh}
      actions={[
        {
          color: 'primary',
          title: translate('auth.refresh.button'),
          onClick: onRefresh,
          disabled: activity.isExpired,
        },
        {
          color: 'primary',
          title: translate('common.logout'),
          onClick: onLogout,
        },
      ]}>
      <DialogContent>
        <Visibility
          visible={!activity.isExpired}
          replacement={<AuthRefreshDialogExpired />}>
          <Stack spacing={2} alignItems={'center'}>
            <IconBadge type={'font'} icon={'hourglass-half'} size={'xl'} />
            <Typography typography={'h2'} textAlign={'center'}>
              {translate('auth.refresh.title')}
            </Typography>
            <Typography typography={'body1'} textAlign={'center'}>
              {translate('auth.refresh.subtitle')}
            </Typography>
            <Typography typography={'h4'} textAlign={'center'}>
              {formatMillis(activity.expiresIn)}
            </Typography>
          </Stack>
        </Visibility>
      </DialogContent>
    </BaseDialog>
  );
};

const AuthRefreshDialogExpired = () => {
  const translate = useTranslate();
  return (
    <Stack spacing={2} alignItems={'center'}>
      <IconBadge type={'font'} icon={'arrow-right-from-bracket'} size={'xl'} />
      <Typography typography={'h2'} textAlign={'center'}>
        {translate('auth.refresh.expired.title')}
      </Typography>
      <Typography typography={'body1'} textAlign={'center'}>
        {translate('auth.refresh.expired.subtitle')}
      </Typography>
    </Stack>
  );
};

const authPaths = [
  '/home',
  '/kyc',
  '/credit-account-payment',
  '/connected-account',
  '/withdrawal',
  '/transfer',
  '/statement-payment',
  '/deposit-onboarding',
  '/savings-close',
  '/bill-payment',
];
const isAuthRequiredPath = (pathname: string) => {
  if (!authPaths.some((path) => pathname.startsWith(path))) return false;
  if (isOpenBankingCallbackPath(pathname)) return false;
  return true;
};
